import { useMemo, useState } from "react";
import { Truck, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useGetExpeditionCountWoRefetchBo } from "@/api/expedition.api";
import type { GetAllExpeditionBoDto } from "@/types/api/api-types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { CarrierFilter } from "@/components/backOffice/validatedExpeditions/useValidatedExpeditionsFilters.reducer";

export type CarrierCounts = {
  all?: number;
  fedex?: number;
  dhlTnt?: number;
};

export const useCarrierStaticCounts = (baseFilter: GetAllExpeditionBoDto): CarrierCounts => {
  const { data: all } = useGetExpeditionCountWoRefetchBo(baseFilter);
  const { data: dhlTnt } = useGetExpeditionCountWoRefetchBo({ ...baseFilter, isDhlTnt: true });

  return useMemo(
    () => ({
      all,
      dhlTnt,
      fedex: all !== undefined && dhlTnt !== undefined ? all - dhlTnt : undefined,
    }),
    [all, dhlTnt],
  );
};

type CarrierOption = {
  value: CarrierFilter;
  label: string;
  textClass: string;
  activeButtonClass: string;
  activeItemClass: string;
  badgeClass: string;
};

const useCarrierOptions = (): CarrierOption[] => {
  const { t } = useTranslation("boLogistics");
  return useMemo(
    () => [
      {
        value: "all",
        label: t("carrierFilter.allTrackings"),
        textClass: "text-gray-700",
        activeButtonClass: "border-black bg-gray-400 text-black",
        activeItemClass: "bg-gray-100",
        badgeClass: "bg-gray-800 text-white",
      },
      {
        value: "fedex",
        label: "Fedex",
        textClass: "text-indigo-600",
        activeButtonClass: "border-indigo-500 bg-indigo-100 text-indigo-600",
        activeItemClass: "bg-indigo-50",
        badgeClass: "bg-indigo-100 text-indigo-600",
      },
      {
        value: "dhlTnt",
        label: "DHL / TNT",
        textClass: "text-amber-600",
        activeButtonClass: "border-amber-500 bg-amber-100 text-amber-600",
        activeItemClass: "bg-amber-50",
        badgeClass: "bg-amber-100 text-amber-600",
      },
    ],
    [t],
  );
};

type Props = {
  carrier: CarrierFilter;
  counts: CarrierCounts;
  onSelect: (carrier: CarrierFilter) => void;
};

export const CarrierFilterButton = ({ carrier, counts, onSelect }: Props) => {
  const { t } = useTranslation("boLogistics");
  const carrierOptions = useCarrierOptions();
  const [open, setOpen] = useState(false);
  const activeOption = carrier !== "all" ? carrierOptions.find((o) => o.value === carrier) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-1.5 rounded-sm border px-4 py-2 text-sm font-semibold transition-all duration-150 hover:opacity-80",
            activeOption
              ? activeOption.activeButtonClass
              : "border-border bg-white text-gray-700 opacity-80",
          )}
        >
          <Truck className="size-4" />
          <span>{activeOption?.label ?? t("carrierFilter.allCarriers")}</span>
          {counts[carrier] !== undefined && <span className="opacity-60">{counts[carrier]}</span>}
          {activeOption && (
            <X
              className="size-4"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                onSelect("all");
              }}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        <div className="flex flex-col">
          {carrierOptions.map((option) => {
            const isActive = carrier === option.value;
            const count = counts[option.value];

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSelect(option.value);
                }}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-gray-100",
                  option.textClass,
                  isActive && cn("font-medium", option.activeItemClass),
                )}
              >
                <span>{option.label}</span>
                {count !== undefined && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-0.5 text-xs font-medium",
                      option.badgeClass,
                    )}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
